import { memo, useEffect, useMemo, useState } from 'react'
import { SwiperOptions } from 'swiper/types'
import { Swiper, SwiperSlide, useSwiperSlide } from 'swiper/react'

const IMGIX_PATH = process.env.IMGIX_PATH ?? ''
const TWICPICS_DOMAIN = process.env.TWICPICS_DOMAIN ?? ''

interface PhotosCarouselProps {
  children?: React.ReactNode
  photos?: string[]
  swipeOptions?: SwiperOptions
  isDisabled?: boolean
  isVisible: boolean
  onChangeIndex?: (index: number) => void
}

const PhotosCarousel = memo(
  ({
    children = null,
    photos = [],
    isDisabled = false,
    swipeOptions,
    isVisible,
    onChangeIndex,
  }: PhotosCarouselProps) => {
    const SLIDES_PER_VIEW = 1

    const [indexActive, setIndexActive] = useState<number>(0)

    const [isFirstRender, setIsFirstRender] = useState<boolean>(true)

    useEffect(() => {
      if (isFirstRender) {
        setIsFirstRender(false)
        return
      }
      onChangeIndex?.(indexActive)
    }, [indexActive])

    const initialPhoto = useMemo(() => {
      if (photos.length > 0) {
        return photos[0]
      }
      return null
    }, [photos])

    return (
      <div className="h-full w-full overflow-hidden rounded-[inherit] bg-[#EBEDF0]">
        {isDisabled && (
          <div className="w-full h-full object-cover ml-auto mr-auto relative overflow-hidden block">
            <div className="w-full h-full absolute z-[0]">
              {initialPhoto && (
                <div className="w-full h-full z-[0]">
                  <ImageContainer
                    index={0}
                    photo={initialPhoto}
                    isVisible={isVisible}
                    indexActive={indexActive}
                    totalPhotos={photos.length}
                    setIndexActive={setIndexActive}
                  />
                </div>
              )}
            </div>
            <div className="!relative top-0 !z-[1] left-0 w-full h-full [background:linear-gradient(180deg,rgba(0,0,0,0.00)_50%,_#000_98%),_linear-gradient(180deg,rgba(0,0,0,0.20)_18.97%,_rgba(0,0,0,0.00)_80.8%)]">
              {children}
            </div>
          </div>
        )}

        {!isDisabled && (
          <Swiper
            loop={photos && photos.length > SLIDES_PER_VIEW}
            slidesPerView={SLIDES_PER_VIEW}
            allowTouchMove
            spaceBetween={0}
            edgeSwipeThreshold={10}
            longSwipesRatio={0.1}
            className="w-full h-full object-cover"
            touchEventsTarget="container"
            {...swipeOptions}
          >
            <div className="w-full h-full absolute z-[0]">
              {photos?.map((photo, index) => {
                return (
                  <SwiperSlide
                    key={`${photo}-${index}`}
                    className=" h-full w-full z-[0]"
                  >
                    <ImageContainer
                      photo={photo}
                      index={index}
                      isVisible={isVisible}
                      indexActive={indexActive}
                      totalPhotos={photos.length}
                      setIndexActive={setIndexActive}
                    />
                  </SwiperSlide>
                )
              })}
            </div>

            <div className="!relative top-0 !z-[1] left-0 w-full h-full [background:linear-gradient(180deg,rgba(0,0,0,0.00)_50%,_#000_98%),_linear-gradient(180deg,rgba(0,0,0,0.20)_18.97%,_rgba(0,0,0,0.00)_80.8%)]">
              {children}
            </div>
          </Swiper>
        )}
      </div>
    )
  }
)

export default PhotosCarousel

const ImageContainer = ({
  photo,
  index,
  isVisible,
  indexActive,
  totalPhotos,
  setIndexActive,
}: {
  photo: string
  index: number
  isVisible: boolean
  indexActive: number
  totalPhotos: number
  setIndexActive: (index: number) => void
}) => {
  const swiperSlide = useSwiperSlide()

  useEffect(() => {
    if (swiperSlide && swiperSlide?.isActive) {
      setIndexActive(index)
    }
  }, [swiperSlide])

  const photoURL = useMemo(() => {
    const deletedURL = IMGIX_PATH
    const path = photo.replace(deletedURL, '')

    if (path.includes(TWICPICS_DOMAIN)) {
      return path
    }
    return `${TWICPICS_DOMAIN}${path}`
  }, [photo])

  const { previousIndex, nextIndex } = useMemo(() => {
    const previousIndex = (indexActive - 1 + totalPhotos) % totalPhotos
    const nextIndex = (indexActive + 1) % totalPhotos
    return { previousIndex, nextIndex }
  }, [indexActive, totalPhotos])

  const isWithinView = (currentIndex: number) => {
    return (
      currentIndex === indexActive ||
      currentIndex === previousIndex ||
      currentIndex === nextIndex
    )
  }

  if (!isWithinView(index) || !isVisible) {
    return <div></div>
  }

  return (
    <div className="w-full h-full object-cover z-[0]">
      <img
        className={`w-full h-full object-cover`}
        src={photoURL}
        alt="property-carousel"
        loading="lazy"
      />
    </div>
  )
}
